/**
 * Design tokens.
 *
 * Specification: ../test/tokens.spec.ts. Master plan §3 K10.
 *
 * One design system consumed by the console, the POS, the KDS, the guest
 * surfaces and the kiosk. archive/v0's `@gf/ui` had ZERO importers — it was
 * built and never wired, which is why this is imported by the POS in the same
 * commit that creates it.
 *
 * Accessibility here is a shipping gate, not a backlog item. The EU
 * Accessibility Act has been in force since 28 Jun 2025 (CZ: zákon 424/2023
 * Sb., enforced by ČOI), guest surfaces and self-service terminals are in
 * scope, and EN 301 549 points at WCAG 2.1 AA today with 2.2 expected. So
 * every colour pair below is CHECKED BY TEST rather than asserted in a comment
 * — a palette that merely looks fine is how products fail an audit.
 *
 * Three densities, because the same component is used a metre from a line
 * cook's wet hands and at arm's length on a manager's laptop.
 *
 * WHY THIS FILE GREW FROM 24 PROPERTIES TO SIXTY.
 *
 * The first version had two surfaces, one border, one font size, one spacing
 * unit and one radius. Everything it could express, it expressed at the same
 * weight — so every panel, tile, dialog and table row in four applications came
 * out as a 1 px box, and the products read as unfinished however carefully they
 * were built. Counted across ~13 000 lines of client UI at that point: one
 * shadow, zero transitions, zero hover rules, twenty font sizes invented ad hoc
 * as `calc(var(--gf-font-base) * 0.68)` and the like, and seventeen spacing
 * multipliers including `* 0.6`, `* 1.1` and `/ 3`.
 *
 * None of that is fixable in a component. A vocabulary with no word for
 * "slightly raised" cannot be used to say it. So the ladder, the scales and the
 * elevation set are here, derived from the same density figures, and the apps
 * spend them rather than inventing them.
 */

// --- Colour ----------------------------------------------------------------

export type ThemeName = "light" | "dark" | "highContrast";

/**
 * Semantic roles, never raw colours.
 *
 * A component asks for `danger`, not for red. That is what lets the
 * high-contrast theme darken every accent at once, and what stops a void
 * button being a slightly different red on each screen.
 *
 * THE ACCENT AND THE PRIMARY ARE DIFFERENT ROLES THAT CURRENTLY HOLD THE SAME
 * RED, and that is the one thing in here that is not obvious.
 *
 * 2026-09-20 made `primary` ink: a black "Zaplatit" beside an outlined red
 * "Storno", because on a till those two must never be confused at speed. On
 * 2026-09-25 the owner chose the look of MyMonday (flat, calm, one accent spent
 * ONLY as a fill) with IteroZen's oxblood as that accent. So:
 *
 *   - `accent` is IDENTITY — the mark, links, the focus ring, a small pip that
 *     says "not sent yet". Never a selected state: selection is neutral
 *     (`surfaceActive`), because a screen full of pink tints is a screen where
 *     the one red that matters no longer stands out.
 *   - `primary` is the fill of THE action a screen exists for — Zaplatit,
 *     Přijmout a uzavřít, Odeslat do kuchyně — ONE per screen, with
 *     `--gf-shadow-glow`. Every other confirming button is neutral.
 *   - `danger` is a visibly DARKER red, and destructive controls stay outlined
 *     rather than filled, except inside a confirmation dialog where the
 *     destructive action is the only one.
 *
 * The separation from "Storno" is now form (outlined vs filled), lightness
 * (pinned at ≥ 1.4 in `tokens.spec.ts`), scarcity (one fill per screen) and
 * wording — `apps/pos/test/ui.spec.ts` already asserts that state is carried by
 * text and not by colour alone. The roles stay separate so the two can diverge
 * again by editing two lines here.
 */
export interface ColorTokens {
  /** The page behind everything. Never the same as `surface`. */
  readonly canvas: string;
  readonly surface: string;
  /** Dialogs, popovers, the row under the cursor. Carries the shadow. */
  readonly surfaceRaised: string;
  /** Inputs, wells, empty states — anything that reads as cut into the page. */
  readonly surfaceSunken: string;
  readonly surfaceHover: string;
  readonly surfaceActive: string;
  /** Retained: 36 existing rules name it. Reads as `surfaceSunken`'s milder twin. */
  readonly surfaceMuted: string;
  readonly text: string;
  readonly textMuted: string;
  /** Identity. See the note above — the mark, links, focus, a pip. */
  readonly accent: string;
  readonly onAccent: string;
  /** A tint to sit `accent` on as text: quiet emphasis, never selection. */
  readonly accentSurface: string;
  /** The fill of the ONE action a screen exists for. See the note above. */
  readonly primary: string;
  readonly onPrimary: string;
  /** Voids, comps, anything that removes money. */
  readonly danger: string;
  readonly onDanger: string;
  readonly dangerSurface: string;
  readonly success: string;
  readonly onSuccess: string;
  readonly successSurface: string;
  /** Degraded selling, unsynced outbox, an expiring certificate. */
  readonly warning: string;
  readonly onWarning: string;
  readonly warningSurface: string;
  /**
   * A control's own boundary, where the border is the only thing identifying it.
   * Holds 3:1 for SC 1.4.11.
   */
  readonly border: string;
  /**
   * Separation between content — table rows, panel edges, dividers.
   *
   * SC 1.4.11 applies to "visual information required to identify user
   * interface components and states". A rule between two rows of a table
   * identifies nothing; the row is identified by its text. Holding such a line
   * to 3:1 is what produced `#767C8A` hairlines everywhere and made four
   * applications look like wireframes.
   *
   * It is exempt from 1.4.11 and NOT exempt from having a floor — see
   * CONTRAST_REQUIREMENTS, where it carries a minimum of its own so that
   * "subtle" can never quietly become "absent".
   */
  readonly borderSubtle: string;
  /** Emphasis: a focused field, a selected card, the edge of a raised surface. */
  readonly borderStrong: string;
  readonly focus: string;
  /**
   * The modal backdrop. Not a hex — it has alpha, by definition.
   *
   * It is a token because it existed as five separate hardcoded copies of
   * `rgb(0 0 0 / 55%)` in the POS alone, plus more elsewhere, and they had
   * already drifted.
   */
  readonly scrim: string;
}

export const THEMES: Readonly<Record<ThemeName, ColorTokens>> = {
  // MyMonday's cool neutral ramp (hue 220), adjusted only where a gate below
  // demands it: `borderSubtle` stays above the 1.35 floor that Monday's own
  // #DFE2E7 misses, and `border` is the lightest grey that still clears 3:1
  // for the controls it is the only boundary of (inputs, outlined buttons).
  light: {
    canvas: "#F5F6F7",
    surface: "#FFFFFF",
    surfaceRaised: "#FFFFFF",
    surfaceSunken: "#F1F2F4",
    surfaceHover: "#F0F2F4",
    surfaceActive: "#E5E7EB",
    surfaceMuted: "#F3F4F6",
    text: "#181C25",
    textMuted: "#636874",
    // IteroZen's oxblood — hsl(352 62% 46%). 5.8:1 under white.
    accent: "#BE2D40",
    onAccent: "#FFFFFF",
    accentSurface: "#FBEEF0",
    primary: "#BE2D40",
    onPrimary: "#FFFFFF",
    danger: "#8C1D18",
    onDanger: "#FFFFFF",
    dangerSurface: "#FBEDEC",
    success: "#15653A",
    onSuccess: "#FFFFFF",
    successSurface: "#E7F4ED",
    warning: "#8A5300",
    onWarning: "#FFFFFF",
    warningSurface: "#FCF2E2",
    border: "#8A909C",
    borderSubtle: "#D9DDE3",
    borderStrong: "#4B515E",
    focus: "#BE2D40",
    scrim: "rgb(16 18 29 / 0.45)",
  },
  dark: {
    canvas: "#0D0E12",
    surface: "#14161B",
    surfaceRaised: "#1C1F26",
    surfaceSunken: "#0A0B0E",
    surfaceHover: "#1F232B",
    surfaceActive: "#262A33",
    surfaceMuted: "#1D2027",
    text: "#F2F4F8",
    textMuted: "#A3ABBA",
    // Accents lighten and their foregrounds invert: a saturated mid-red on
    // near-black fails AA, however good it looks in a mockup.
    //
    // NOT IteroZen's dark #D34558: it fails 4.5:1 against every ink there is
    // (4.41 under near-black, 4.10 as text on the surface). #E05060 is the
    // lightest oxblood that clears both.
    accent: "#E05060",
    onAccent: "#0B0D12",
    accentSurface: "#2B1418",
    primary: "#E05060",
    onPrimary: "#0B0D12",
    danger: "#FF9A92",
    onDanger: "#0B0D12",
    dangerSurface: "#2C1512",
    success: "#69D89A",
    onSuccess: "#0B0D12",
    successSurface: "#102419",
    warning: "#F0B93C",
    onWarning: "#0B0D12",
    warningSurface: "#2A1F07",
    border: "#79808F",
    borderSubtle: "#2E343E",
    borderStrong: "#A3ABBA",
    focus: "#E05060",
    scrim: "rgb(0 0 0 / 0.66)",
  },
  highContrast: {
    // Targets AAA (7:1) rather than AA. Required in practice for a kiosk in
    // direct sun and for staff who need it, and it costs nothing to ship.
    //
    // THE DEPTH LADDER DELIBERATELY COLLAPSES HERE. Every surface is white and
    // every border is black: separation is carried by the border, not by a
    // difference of four per cent in lightness that this theme's whole purpose
    // is to reject. `SHADOWS.highContrast` is `none` for the same reason.
    canvas: "#FFFFFF",
    surface: "#FFFFFF",
    surfaceRaised: "#FFFFFF",
    surfaceSunken: "#FFFFFF",
    surfaceHover: "#E8E8E8",
    surfaceActive: "#D6D6D6",
    surfaceMuted: "#FFFFFF",
    text: "#000000",
    textMuted: "#000000",
    accent: "#7A0F1F",
    onAccent: "#FFFFFF",
    accentSurface: "#FFFFFF",
    primary: "#7A0F1F",
    onPrimary: "#FFFFFF",
    danger: "#8A0000",
    onDanger: "#FFFFFF",
    dangerSurface: "#FFFFFF",
    success: "#00461F",
    onSuccess: "#FFFFFF",
    successSurface: "#FFFFFF",
    warning: "#5E3600",
    onWarning: "#FFFFFF",
    warningSurface: "#FFFFFF",
    border: "#000000",
    borderSubtle: "#000000",
    borderStrong: "#000000",
    focus: "#7A0F1F",
    scrim: "rgb(0 0 0 / 0.80)",
  },
};

/** Foreground/background pairs that must pass, and the ratio each must reach. */
export const CONTRAST_REQUIREMENTS: readonly {
  readonly foreground: keyof ColorTokens;
  readonly background: keyof ColorTokens;
  /** 4.5 for body text, 3.0 for UI components and focus rings (SC 1.4.11). */
  readonly minimum: number;
}[] = [
  // Body text against every surface it can land on. Adding a rung to the ladder
  // without adding it here would be a surface nothing had ever been read on.
  { foreground: "text", background: "surface", minimum: 4.5 },
  { foreground: "text", background: "surfaceMuted", minimum: 4.5 },
  { foreground: "text", background: "canvas", minimum: 4.5 },
  { foreground: "text", background: "surfaceRaised", minimum: 4.5 },
  { foreground: "text", background: "surfaceSunken", minimum: 4.5 },
  { foreground: "text", background: "surfaceHover", minimum: 4.5 },
  { foreground: "text", background: "surfaceActive", minimum: 4.5 },
  { foreground: "textMuted", background: "surface", minimum: 4.5 },
  { foreground: "textMuted", background: "canvas", minimum: 4.5 },
  { foreground: "textMuted", background: "surfaceSunken", minimum: 4.5 },

  { foreground: "onPrimary", background: "primary", minimum: 4.5 },
  { foreground: "onAccent", background: "accent", minimum: 4.5 },
  { foreground: "onDanger", background: "danger", minimum: 4.5 },
  { foreground: "onSuccess", background: "success", minimum: 4.5 },
  { foreground: "onWarning", background: "warning", minimum: 4.5 },

  // The tinted bands. A status colour used as TEXT on its own tint is the whole
  // point of these roles — a solid amber block is loud enough to be ignored.
  { foreground: "accent", background: "accentSurface", minimum: 4.5 },
  { foreground: "accent", background: "surface", minimum: 4.5 },
  { foreground: "danger", background: "dangerSurface", minimum: 4.5 },
  { foreground: "success", background: "successSurface", minimum: 4.5 },
  { foreground: "warning", background: "warningSurface", minimum: 4.5 },

  { foreground: "border", background: "surface", minimum: 3.0 },
  { foreground: "borderStrong", background: "surface", minimum: 3.0 },
  { foreground: "focus", background: "surface", minimum: 3.0 },

  /**
   * The exemption, written down with a floor rather than taken silently.
   *
   * SC 1.4.11 does not reach a decorative divider, so this is not 3.0. But an
   * unbounded "subtle" is how a hairline becomes invisible three refinements
   * later, and a border nobody can see is a layout nobody can parse. 1.35 is
   * just under the lightest line that still reads as a line on both themes
   * (light 1.39, dark 1.45).
   */
  { foreground: "borderSubtle", background: "surface", minimum: 1.35 },
];

// --- Contrast --------------------------------------------------------------

function channel(value: number): number {
  const c = value / 255;
  return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
}

const HEX = /^#[0-9A-F]{6}$/i;

/**
 * WCAG 2.x relative luminance.
 *
 * IT REFUSES ANYTHING THAT IS NOT AN OPAQUE `#RRGGBB`, and that is a fix rather
 * than a nicety. This used to be `Number.parseInt(hex.replace("#", ""), 16)`
 * followed by three bitwise extractions. Given `rgb(0 0 0 / 0.55)` the parse
 * yields NaN — and `NaN >> 16` is 0, because bitwise operators coerce. So the
 * function returned a luminance of ZERO: a translucent backdrop scored as pure
 * black, 21:1 against white, and would have passed every contrast requirement
 * it was ever named in while meaning nothing at all.
 *
 * It was unreachable while every colour in the palette was hex. `scrim` made it
 * reachable, which is the only reason anybody looked.
 */
export function relativeLuminance(hex: string): number {
  if (!HEX.test(hex)) {
    throw new Error(
      `"${hex}" is not an opaque #RRGGBB colour, so it has no WCAG relative luminance. ` +
        `Scoring it would silently return black.`,
    );
  }
  const n = Number.parseInt(hex.slice(1), 16);
  const r = channel((n >> 16) & 255);
  const g = channel((n >> 8) & 255);
  const b = channel(n & 255);
  return 0.2126 * r + 0.7152 * g + 0.0722 * b;
}

/** WCAG 2.x contrast ratio, 1..21. */
export function contrastRatio(a: string, b: string): number {
  const la = relativeLuminance(a);
  const lb = relativeLuminance(b);
  const [lighter, darker] = la >= lb ? [la, lb] : [lb, la];
  return (lighter + 0.05) / (darker + 0.05);
}

export interface ContrastFailure {
  readonly theme: ThemeName;
  readonly foreground: keyof ColorTokens;
  readonly background: keyof ColorTokens;
  readonly ratio: number;
  readonly minimum: number;
}

/**
 * Every failing pair across every theme.
 *
 * Run as a test AND available to a CI gate, because "we checked the palette
 * once" is exactly how a theme drifts out of compliance three tickets later.
 *
 * `requirements` is injectable ONLY so that the refusal below can be exercised
 * against a requirement that names a translucent colour. A guard that cannot be
 * driven from a test is a guard nobody knows the state of.
 */
export function contrastFailures(
  minimumOverride?: Readonly<Partial<Record<ThemeName, number>>>,
  requirements: typeof CONTRAST_REQUIREMENTS = CONTRAST_REQUIREMENTS,
): ContrastFailure[] {
  const failures: ContrastFailure[] = [];

  for (const [name, colors] of Object.entries(THEMES) as [ThemeName, ColorTokens][]) {
    for (const requirement of requirements) {
      for (const role of [requirement.foreground, requirement.background] as const) {
        if (!HEX.test(colors[role])) {
          throw new Error(
            `${name}.${role} is "${colors[role]}", which has no WCAG luminance. ` +
              `A contrast requirement may only name an opaque #RRGGBB colour.`,
          );
        }
      }

      const minimum = minimumOverride?.[name] ?? requirement.minimum;
      const ratio = contrastRatio(colors[requirement.foreground], colors[requirement.background]);
      if (ratio < minimum) {
        failures.push({
          theme: name,
          foreground: requirement.foreground,
          background: requirement.background,
          ratio,
          minimum,
        });
      }
    }
  }

  return failures;
}

// --- Elevation -------------------------------------------------------------

export interface ShadowTokens {
  /** Something raised a hair off the page: a pressed-in-place control, a chip. */
  readonly sm: string;
  /** Raised: a sticky bar, a card under the pointer, a sheet edge. */
  readonly md: string;
  /** Has LEFT the page: dialogs, the palette, drawers, toasts. Nothing else. */
  readonly lg: string;
  /** The primary button: a halo in the accent and a lit top edge (IteroZen). */
  readonly glow: string;
}

/**
 * Depth, per theme, because a shadow is a light-theme instrument.
 *
 * On a near-black surface a drop shadow is invisible — there is nothing darker
 * to cast onto. Dark themes carry elevation by making the raised surface
 * LIGHTER, and sell it with a one-pixel inset highlight along the top edge,
 * which is the light catching a lifted panel. That is why `surfaceRaised` moves
 * in both themes and the shadow only does real work in one.
 *
 * `highContrast` has none at all: it separates with a black border, and a soft
 * grey blur is the opposite of what somebody choosing that theme asked for.
 *
 * MONDAY'S RULE (2026-09-25): a drop shadow means "this has left the page".
 * Cards and panels at rest carry NO shadow — their edge is a one-pixel
 * `borderSubtle` border, which looks exactly like MyMonday's box-shadow ring
 * and, unlike a ring, survives the high-contrast theme. `lg` is Monday's
 * overlay shadow verbatim; `sm`/`md` are its `raised` step.
 */
export const SHADOWS: Readonly<Record<ThemeName, ShadowTokens>> = {
  light: {
    sm: "0 1px 2px rgb(15 23 42 / 0.05)",
    md: "0 1px 2px rgb(15 23 42 / 0.05), 0 4px 12px -4px rgb(15 23 42 / 0.10)",
    lg: "0 16px 40px -12px rgb(15 23 42 / 0.18), 0 2px 8px rgb(15 23 42 / 0.06)",
    glow: "0 0 16px rgb(190 45 64 / 0.18), inset 0 1px 0 rgb(255 255 255 / 0.22)",
  },
  dark: {
    sm: "0 1px 2px rgb(0 0 0 / 0.40)",
    md: "0 1px 2px rgb(0 0 0 / 0.40), inset 0 1px 0 rgb(255 255 255 / 0.04)",
    lg: "0 24px 56px -16px rgb(0 0 0 / 0.80), 0 4px 12px rgb(0 0 0 / 0.40), inset 0 1px 0 rgb(255 255 255 / 0.06)",
    glow: "0 0 20px rgb(224 80 96 / 0.22), inset 0 1px 0 rgb(255 255 255 / 0.18)",
  },
  highContrast: { sm: "none", md: "none", lg: "none", glow: "none" },
};

// --- Density ---------------------------------------------------------------

/**
 * THE TYPEFACE, which had no token and therefore no answer.
 *
 * Every app rendered in the browser's default — a serif, on a till and on a
 * back office full of tabular figures. It looked like an unstyled document,
 * which is precisely what it was.
 *
 * IT USED TO BE A SYSTEM STACK, and the argument for that was good: a till must
 * start offline on a device that has never had a network, and a webfont is a
 * request that either blocks first paint or swaps under a server's hand
 * mid-service. What it bought was a product that rendered in Segoe on a Windows
 * till, SF on an iPad and Roboto on a handheld — three different products, none
 * of which anybody had designed.
 *
 * Both halves of the objection are answered rather than dropped, and the answers
 * live in `@gf/ui`:
 *
 *   - offline, because `tools/vite-sw` precaches every `.woff2` alongside the
 *     scripts and the SQLite binary, so a till that has been switched on once
 *     holds the font for ever;
 *   - no reflow, because `font-display: swap` paints immediately in a fallback
 *     face that carries INTER'S OWN vertical metrics, so the swap cannot move a
 *     line box.
 *
 * The old stack is still here, last. If a font file is ever missing the product
 * degrades to precisely what it was before.
 *
 * `"Inter"` is the subset in `@gf/ui/fonts` — Latin, Latin Extended-A and the
 * Ukrainian Cyrillic range, so Czech, Slovak and Ukrainian are all covered by
 * the real face rather than by whatever the device had lying around.
 */
export const FONT_STACK =
  '"Inter", "Inter fallback", system-ui, -apple-system, "Segoe UI", Roboto, "Helvetica Neue", Arial, sans-serif';

/** Receipt numbers, EICs, event ids — anything read character by character. */
export const FONT_STACK_MONO =
  'ui-monospace, SFMono-Regular, "SF Mono", Menlo, Consolas, "Liberation Mono", monospace';

export type DensityName = "pos" | "comfortable" | "kiosk";

export interface DensityTokens {
  /**
   * Minimum interactive target, in CSS px.
   *
   * WCAG 2.2 SC 2.5.8 sets a floor of 24, which is far too small for the
   * actual use: a line cook with wet hands, at speed, during a rush. These are
   * deliberately well above it.
   */
  readonly targetMin: number;
  readonly fontBase: number;
  readonly spaceUnit: number;
  readonly radius: number;
}

export const DENSITIES: Readonly<Record<DensityName, DensityTokens>> = {
  // Large-touch. The product grid a server hits a hundred times a shift.
  pos: { targetMin: 56, fontBase: 17, spaceUnit: 8, radius: 10 },
  // A manager's laptop, at arm's length, with a mouse. 14 px is MyMonday's
  // body size, and its density is most of why that product reads as calm.
  comfortable: { targetMin: 40, fontBase: 14, spaceUnit: 6, radius: 8 },
  // Reach-range and standing, often by someone who has never seen it before.
  kiosk: { targetMin: 72, fontBase: 21, spaceUnit: 12, radius: 12 },
};

/** The WCAG 2.2 SC 2.5.8 floor, kept explicit so the margin above it is visible. */
export const WCAG_MINIMUM_TARGET_PX = 24;

// --- Scales ----------------------------------------------------------------

/**
 * The spacing ramp, as multiples of the density's unit.
 *
 * Nine steps, and the gaps between them widen — 0.25, 0.5, 1, 1.5, 2, 3, 4, 6,
 * 8 — because that is how spacing is actually used: the small end needs fine
 * control for the inside of a control, and the large end only ever needs to be
 * obviously larger than the step below it.
 *
 * It replaces seventeen multipliers invented at the call site, of which `* 0.6`,
 * `* 1.1`, `* 0.2` and `/ 3` were four. None of them was wrong on its own; the
 * absence of a rhythm was the problem, and a rhythm is a decision that has to
 * live in one place.
 */
export const SPACE_STEPS: readonly number[] = [0.25, 0.5, 1, 1.5, 2, 3, 4, 6, 8];

/**
 * The type ramp, as multiples of the density's base size.
 *
 * `xs` and `sm` are for eyebrow labels, metadata and table captions; `base` is
 * body; everything above it is a heading. The top end is deliberately far from
 * the bottom: a figure a manager is meant to read across a desk, or a table
 * number a cook is meant to read across a kitchen, has to be a different KIND
 * of thing from its label, not one notch bigger.
 */
export const TEXT_STEPS: Readonly<Record<string, number>> = {
  // 11 / 13 at office density — MyMonday's badge and small-text sizes. Its
  // 12 px label role falls between them and is not worth a ninth step.
  xs: 0.8,
  sm: 0.93,
  base: 1,
  md: 1.125,
  lg: 1.3,
  xl: 1.6,
  "2xl": 2,
  "3xl": 2.6,
};

/**
 * Easing, which durations without curves cannot express.
 *
 * `out` for anything the user started — a press, a dialog opening. It moves
 * fast at first so the interface feels like it answered before it finished
 * moving. `in-out` only for something that moves on its own.
 */
export const EASING = {
  out: "cubic-bezier(0.2, 0.8, 0.3, 1)",
  inOut: "cubic-bezier(0.4, 0, 0.2, 1)",
} as const;

/**
 * Stacking, in one place.
 *
 * It was 1, 3, 20, 25 and 40, each picked in a different file on a different
 * day — and `.pos__overlay`, the modal backdrop itself, had no z-index at all
 * and worked only by document order.
 */
export const Z_INDEX = {
  base: 0,
  sticky: 10,
  overlay: 100,
  modal: 200,
  toast: 300,
} as const;

// --- Motion ----------------------------------------------------------------

export interface MotionTokens {
  readonly fast: number;
  readonly base: number;
  readonly slow: number;
}

export const MOTION: MotionTokens = { fast: 90, base: 160, slow: 240 };

/**
 * Motion honouring `prefers-reduced-motion`.
 *
 * Zero, not merely shorter. Vestibular triggers are not a matter of degree,
 * and a POS animating a full-screen transition is a genuinely unpleasant
 * surface to work at for eight hours.
 */
export const motionFor = (reduced: boolean): MotionTokens =>
  reduced ? { fast: 0, base: 0, slow: 0 } : MOTION;

// --- CSS emission ----------------------------------------------------------

/**
 * Tokens as CSS custom properties.
 *
 * Custom properties rather than a build-time constant so a theme switch is one
 * attribute on `<html>` — no re-render, no flash, and no second bundle.
 * Deterministic ordering so the emitted stylesheet diffs cleanly.
 */
export function cssVariables(
  theme: ThemeName,
  density: DensityName,
  reducedMotion = false,
): string {
  const colors = THEMES[theme];
  const sizes = DENSITIES[density];
  const shadows = SHADOWS[theme];
  const motion = motionFor(reducedMotion);

  const lines = [
    ...Object.entries(colors)
      .sort(([a], [b]) => (a < b ? -1 : 1))
      .map(([name, value]) => `--gf-color-${kebab(name)}: ${value};`),

    `--gf-font-family: ${FONT_STACK};`,
    `--gf-font-mono: ${FONT_STACK_MONO};`,
    `--gf-target-min: ${sizes.targetMin}px;`,
    `--gf-font-base: ${sizes.fontBase}px;`,
    `--gf-space: ${sizes.spaceUnit}px;`,
    `--gf-radius: ${sizes.radius}px;`,

    // Rounded, because a half-pixel gap renders as a seam on one row of a table
    // and not on the next, and the density units divide unevenly by design.
    ...SPACE_STEPS.map((step, i) => `--gf-space-${i}: ${Math.round(sizes.spaceUnit * step)}px;`),
    ...Object.entries(TEXT_STEPS).map(
      ([name, step]) => `--gf-text-${name}: ${Math.round(sizes.fontBase * step)}px;`,
    ),

    `--gf-leading-tight: 1.15;`,
    `--gf-leading-normal: 1.45;`,
    `--gf-leading-relaxed: 1.65;`,
    `--gf-tracking-tight: -0.02em;`,
    `--gf-tracking-normal: 0em;`,
    `--gf-tracking-wide: 0.06em;`,
    `--gf-weight-normal: 400;`,
    `--gf-weight-medium: 500;`,
    `--gf-weight-semibold: 600;`,
    `--gf-weight-bold: 700;`,

    // Four tiers that name what a thing IS, not how big it is: `sm` a key cap
    // or a menu row, `md` a control, `lg` a card or panel, `xl` anything that
    // has left the page (dialog, sheet, palette). At office density this is
    // MyMonday's 6 / 8 / 12 / 16 exactly.
    `--gf-radius-sm: ${Math.round(sizes.radius * 0.75)}px;`,
    `--gf-radius-md: ${sizes.radius}px;`,
    `--gf-radius-lg: ${Math.round(sizes.radius * 1.5)}px;`,
    `--gf-radius-xl: ${sizes.radius * 2}px;`,
    `--gf-radius-full: 999px;`,
    `--gf-border-thin: 1px;`,
    `--gf-border-thick: 2px;`,

    `--gf-shadow-sm: ${shadows.sm};`,
    `--gf-shadow-md: ${shadows.md};`,
    `--gf-shadow-lg: ${shadows.lg};`,
    `--gf-shadow-glow: ${shadows.glow};`,

    // A focus ring is the one place a thick line is not decoration. It scales
    // with the density because the device it is seen on does.
    `--gf-focus-width: ${sizes.targetMin >= 56 ? 3 : 2}px;`,
    `--gf-focus-offset: 2px;`,

    `--gf-motion-fast: ${motion.fast}ms;`,
    `--gf-motion-base: ${motion.base}ms;`,
    `--gf-motion-slow: ${motion.slow}ms;`,
    `--gf-ease-out: ${EASING.out};`,
    `--gf-ease-in-out: ${EASING.inOut};`,

    ...Object.entries(Z_INDEX).map(([name, value]) => `--gf-z-${name}: ${String(value)};`),
  ];

  return lines.join("\n");
}

const kebab = (value: string): string => value.replace(/[A-Z]/g, (c) => `-${c.toLowerCase()}`);
